// Siluetas: rects y círculos monocromos, sin datos reales del tenant — la
// idea es que se lea la disposición de la página de un vistazo, no que sea
// una vista previa. `silhouette` es la miniatura del selector (se lee a
// 40px); `preview` es la versión de página completa que muestra el modal de
// confirmación, con el header (logo + nombre) que todos los modos comparten
// y `previewLabels` para nombrar cada zona (qué parte son enlaces/bloques y
// qué parte son productos).
//
// Ritmo vertical del preview (viewBox 120x160), para que los títulos de cada
// zona no queden pegados al header ni a las formas de abajo:
//   0-50    header (logo, nombre, descripción)
//   66      línea base del título de la primera zona
//   72      arranque del contenido de la primera zona
//   ~148    fondo del contenido, con ~12 de margen inferior
const PREVIEW_HEADER = [
    { type: "circle", cx: 60, cy: 22, r: 10 },
    { type: "rect", x: 40, y: 38, width: 40, height: 5, rx: 2.5 },
    { type: "rect", x: 48, y: 47, width: 24, height: 3, rx: 1.5 }
];

export const DISPLAY_MODE_SILHOUETTE_VIEW_BOX = "0 0 64 48";
export const DISPLAY_MODE_PREVIEW_VIEW_BOX = "0 0 120 160";

export const DISPLAY_MODE_OPTIONS = [
    {
        value: "feed",
        icon: "🔗",
        label: "Feed",
        description: "Solo enlaces, uno debajo del otro.",
        silhouette: [
            { type: "rect", x: 6, y: 6, width: 52, height: 8, rx: 4 },
            { type: "rect", x: 6, y: 20, width: 52, height: 8, rx: 4 },
            { type: "rect", x: 6, y: 34, width: 52, height: 8, rx: 4 }
        ],
        preview: [
            ...PREVIEW_HEADER,
            { type: "rect", x: 16, y: 72, width: 88, height: 14, rx: 7 },
            { type: "rect", x: 16, y: 92, width: 88, height: 14, rx: 7 },
            { type: "rect", x: 16, y: 112, width: 88, height: 14, rx: 7 },
            { type: "rect", x: 16, y: 132, width: 88, height: 14, rx: 7 }
        ],
        previewLabels: [{ text: "Tus enlaces", y: 66 }]
    },
    {
        value: "catalog",
        icon: "🛍️",
        label: "Catálogo",
        description: "Tus enlaces arriba y tu vitrina de productos debajo.",
        silhouette: [
            { type: "rect", x: 4, y: 4, width: 16, height: 16, rx: 2 },
            { type: "rect", x: 24, y: 4, width: 16, height: 16, rx: 2 },
            { type: "rect", x: 44, y: 4, width: 16, height: 16, rx: 2 },
            { type: "rect", x: 4, y: 24, width: 16, height: 16, rx: 2 },
            { type: "rect", x: 24, y: 24, width: 16, height: 16, rx: 2 },
            { type: "rect", x: 44, y: 24, width: 16, height: 16, rx: 2 }
        ],
        preview: [
            ...PREVIEW_HEADER,
            { type: "rect", x: 16, y: 72, width: 40, height: 30, rx: 3 },
            { type: "rect", x: 16, y: 105, width: 26, height: 4, rx: 2 },
            { type: "rect", x: 64, y: 72, width: 40, height: 30, rx: 3 },
            { type: "rect", x: 64, y: 105, width: 26, height: 4, rx: 2 },
            { type: "rect", x: 16, y: 116, width: 40, height: 30, rx: 3 },
            { type: "rect", x: 16, y: 149, width: 26, height: 4, rx: 2 },
            { type: "rect", x: 64, y: 116, width: 40, height: 30, rx: 3 },
            { type: "rect", x: 64, y: 149, width: 26, height: 4, rx: 2 }
        ],
        previewLabels: [{ text: "Tus productos", y: 66 }]
    },
    {
        value: "segments",
        icon: "🧩",
        label: "Bloques",
        description:
            "Solo los bloques que armaste (enlaces, banners, carruseles).",
        silhouette: [
            { type: "rect", x: 4, y: 4, width: 56, height: 12, rx: 3 },
            { type: "rect", x: 4, y: 22, width: 12, height: 12, rx: 6 },
            { type: "rect", x: 20, y: 22, width: 12, height: 12, rx: 6 },
            { type: "rect", x: 36, y: 22, width: 12, height: 12, rx: 6 },
            { type: "rect", x: 4, y: 38, width: 26, height: 8, rx: 2 },
            { type: "rect", x: 34, y: 38, width: 26, height: 8, rx: 2 }
        ],
        preview: [
            ...PREVIEW_HEADER,
            { type: "rect", x: 16, y: 72, width: 88, height: 24, rx: 4 },
            { type: "circle", cx: 32, cy: 112, r: 9 },
            { type: "circle", cx: 60, cy: 112, r: 9 },
            { type: "circle", cx: 88, cy: 112, r: 9 },
            { type: "rect", x: 16, y: 126, width: 88, height: 12, rx: 6 },
            { type: "rect", x: 16, y: 142, width: 88, height: 12, rx: 6 }
        ],
        previewLabels: [{ text: "Tus bloques", y: 66 }]
    },
    {
        value: "mix",
        icon: "🔀",
        label: "Mixto",
        // El catálogo real no apila las dos zonas: muestra un switch
        // Home/Tienda y cada pestaña rinde una sola de ellas (ver
        // showMixTabs en CatalogPreviewCard). Por eso la silueta lleva el
        // switch arriba y las dos zonas lado a lado, como dos vistas
        // alternativas y no como una página de dos secciones.
        description: "Un switch entre tus bloques y tu vitrina de productos.",
        silhouette: [
            // Switch Home/Tienda: riel tenue con la pestaña activa encima.
            {
                type: "rect",
                x: 4,
                y: 3,
                width: 56,
                height: 9,
                rx: 4.5,
                opacity: 0.3
            },
            { type: "rect", x: 5, y: 4, width: 27, height: 7, rx: 3.5 },
            // Pestaña Home: bloques.
            { type: "rect", x: 4, y: 17, width: 26, height: 8, rx: 2 },
            { type: "rect", x: 4, y: 28, width: 26, height: 6, rx: 2 },
            { type: "rect", x: 4, y: 37, width: 26, height: 6, rx: 2 },
            // Pestaña Tienda: vitrina.
            { type: "rect", x: 34, y: 17, width: 11, height: 11, rx: 2 },
            { type: "rect", x: 49, y: 17, width: 11, height: 11, rx: 2 },
            { type: "rect", x: 34, y: 32, width: 11, height: 11, rx: 2 },
            { type: "rect", x: 49, y: 32, width: 11, height: 11, rx: 2 }
        ],
        preview: [
            ...PREVIEW_HEADER,
            // Switch Home/Tienda.
            {
                type: "rect",
                x: 16,
                y: 58,
                width: 88,
                height: 13,
                rx: 6.5,
                opacity: 0.3
            },
            { type: "rect", x: 17.5, y: 59.5, width: 42, height: 10, rx: 5 },
            // Pestaña Home: bloques.
            { type: "rect", x: 16, y: 90, width: 40, height: 14, rx: 3 },
            { type: "circle", cx: 22, cy: 116, r: 6 },
            { type: "circle", cx: 36, cy: 116, r: 6 },
            { type: "circle", cx: 50, cy: 116, r: 6 },
            { type: "rect", x: 16, y: 128, width: 40, height: 8, rx: 4 },
            { type: "rect", x: 16, y: 140, width: 40, height: 8, rx: 4 },
            // Pestaña Tienda: vitrina.
            { type: "rect", x: 64, y: 90, width: 18, height: 22, rx: 3 },
            { type: "rect", x: 86, y: 90, width: 18, height: 22, rx: 3 },
            { type: "rect", x: 64, y: 114, width: 12, height: 3, rx: 1.5 },
            { type: "rect", x: 86, y: 114, width: 12, height: 3, rx: 1.5 },
            { type: "rect", x: 64, y: 121, width: 18, height: 22, rx: 3 },
            { type: "rect", x: 86, y: 121, width: 18, height: 22, rx: 3 },
            { type: "rect", x: 64, y: 145, width: 12, height: 3, rx: 1.5 },
            { type: "rect", x: 86, y: 145, width: 12, height: 3, rx: 1.5 }
        ],
        previewLabels: [
            { text: "Bloques", x: 16, y: 84 },
            { text: "Productos", x: 64, y: 84 }
        ]
    }
];

// Modos con editor de bloques acotado: hasta 2 bloques de enlaces o banners
// pequeños. Al llegar desde 'segments'/'mix' el backend deja un solo bloque
// con los enlaces que permite la cuota `customLinks` del plan.
export const LINKS_ONLY_DISPLAY_MODES = ["feed", "catalog"];

export const LINKS_ONLY_MAX_SEGMENTS = 2;
export const LINKS_ONLY_SEGMENT_TYPES = ["links", "banners"];
export const LINKS_ONLY_BANNER_SIZE_TIER = "sm";

// Textos por `reason` de DISPLAY_MODE_SEGMENTS_INVALID (mismo contrato que el
// backend); los usa la validación previa al guardar y el error del backend.
export const LINKS_ONLY_VIOLATION_MESSAGES = {
    "max-segments":
        "En esta vista puedes tener hasta 2 bloques. Quita uno para guardar.",
    "segment-type":
        "En esta vista solo se pueden usar bloques de enlaces o de banners. Quita los demás para guardar.",
    "banner-style":
        "En esta vista los banners solo pueden ir en estilos pequeños. Cambia el estilo para guardar."
};

// `feed` es la única vista sin buscador en el catálogo público.
export const displayModeUsesSearchBar = (displayMode) => displayMode !== "feed";

// catalog-recurso-unico: solo estas vistas muestran productos, así que solo
// en ellas tiene sentido elegir la tarjeta de producto.
export const PRODUCT_DISPLAY_MODES = ["catalog", "mix"];

export const displayModeShowsProducts = (displayMode) =>
    PRODUCT_DISPLAY_MODES.includes(displayMode);

export const findDisplayModeOption = (value) =>
    DISPLAY_MODE_OPTIONS.find((option) => option.value === value) ?? null;
